import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFacebookAuth } from "@/contexts/FacebookAuthContext";
import ApperIcon from "@/components/ApperIcon";
import Select from "@/components/atoms/Select";
import Button from "@/components/atoms/Button";

const FacebookAccountSelector = () => {
  const navigate = useNavigate();
  const {
    isAuthenticated,
    user,
    businessAccounts,
    selectedBusinessAccount,
    adAccounts,
    selectedAdAccount,
    pages,
    selectedPage,
    loading,
    selectBusinessAccount,
    selectAdAccount,
    selectPage,
    logout
  } = useFacebookAuth();

  // Memoize computed values to prevent unnecessary re-renders
  const hasBusinessAccounts = useMemo(() =>
    Array.isArray(businessAccounts) && businessAccounts.length > 0,
    [businessAccounts]
  );

  const hasAdAccounts = useMemo(() =>
    Array.isArray(adAccounts) && adAccounts.length > 0,
    [adAccounts]
  );
  const hasPages = useMemo(() =>
    Array.isArray(pages) && pages.length > 0,
    [pages]
  );

// Handle business account selection with error handling
  const handleBusinessAccountChange = async (event) => {
    try {
      const value = event?.target?.value;
      if (!value || !businessAccounts) return;

      const businessAccount = businessAccounts.find(ba => ba && ba.id === value);
      if (businessAccount) {
        await selectBusinessAccount(businessAccount);
      }
    } catch (error) {
      console.error('Error selecting business account:', error.message);
      if (error.message?.includes('Unauthorized')) {
        console.error('Authentication may have expired. Please log in again.');
      }
    }
  };

  // Handle ad account selection with error handling
  const handleAdAccountChange = async (event) => {
    try {
      const value = event?.target?.value;
      if (!value || !adAccounts) return;

      const adAccount = adAccounts.find(aa => aa && aa.id === value);
      if (adAccount) {
        await selectAdAccount(adAccount);
      }
    } catch (error) {
      console.error('Error selecting ad account:', error.message);
      if (error.message?.includes('Unauthorized')) {
        console.error('Authentication may have expired. Please log in again.');
      }
    }
  };

  const handlePageChange = (event) => {
    try {
      const value = event?.target?.value;
      if (!value || !pages) return;

      const page = pages.find(pg => pg && pg.id === value);
      if (page) {
        selectPage(page);
      }
    } catch (error) {
      console.error('Error selecting page:', error.message);
      if (error.message?.includes('Unauthorized')) {
        console.error('Authentication may have expired. Please log in again.');
      }
    }
  };

  // Handle logout with error handling
  const handleLogout = () => {
    try {
      logout();
    } catch (error) {
      console.error('Error during logout:', error.message);
      // Still try to navigate away even if logout fails
      navigate('/facebook/login');
    }
  };

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:space-x-3">
      {/* Business Account Selector */}
      <div className="min-w-0">
        <Select
          value={selectedBusinessAccount?.id || ""}
          onChange={handleBusinessAccountChange}
          placeholder={
            !isAuthenticated
              ? "Connect to load businesses"
              : hasBusinessAccounts
                ? "Select Business"
                : "No businesses"
          }
          className="text-sm min-w-[200px]"
          disabled={!isAuthenticated || loading || !hasBusinessAccounts}
          options={hasBusinessAccounts ? businessAccounts.map(account => ({
            value: account.id,
            label: account.name
          })) : []}
        />
      </div>
      
      {/* Ad Account Selector */}
      <div className="min-w-0">
        <Select
          value={selectedAdAccount?.id || ""}
          onChange={handleAdAccountChange}
          placeholder={
            !isAuthenticated
              ? "Connect to load ad accounts"
              : !selectedBusinessAccount
                ? "Select a business first"
                : hasAdAccounts
                  ? "Select Ad Account"
                  : "No ad accounts"
          }
          className="text-sm min-w-[200px]"
          disabled={!isAuthenticated || loading || !selectedBusinessAccount || !hasAdAccounts}
          options={hasAdAccounts ? adAccounts.map(account => ({
            value: account.id,
            label: account.name
          })) : []}
        />
      </div>

      {/* Page Selector */}
      <div className="min-w-0">
        <Select
          value={selectedPage?.id || ""}
          onChange={handlePageChange}
          placeholder={
            !isAuthenticated
              ? "Connect to load pages"
              : !selectedAdAccount
                ? "Select an ad account first"
                : hasPages
                  ? "Select Page"
                  : "No pages"
          }
          className="text-sm min-w-[200px]"
          disabled={!isAuthenticated || loading || !selectedAdAccount || !hasPages}
          options={hasPages ? pages.map(page => ({
            value: page.id,
            label: page.name
          })) : []}
        />
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-3">
        {isAuthenticated ? (
          <>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-facebook to-facebook-hover rounded-full flex items-center justify-center text-white text-sm font-semibold">
                {user?.name ? user.name[0] : "FB"}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user?.name || "Facebook User"}</p>
                <p className="text-xs text-gray-500">Connected</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-gray-700"
              title="Logout"
            >
              <ApperIcon name="LogOut" className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <Button
            onClick={() => navigate("/facebook/login")}
            size="sm"
            className="inline-flex items-center gap-2 bg-facebook hover:bg-facebook-hover text-white"
          >
            <ApperIcon name="Facebook" className="w-4 h-4" />
            Connect Facebook
          </Button>
        )}
      </div>
    </div>
  );
};

export default FacebookAccountSelector;